import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { gameAdd } from "../../services/game.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  handleServerValidations,
  globalLoader,
} from "../../../../utils/commonfunction";

const GameAdd = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Games"), url: "/admin/game-management/game/list/1" },
    { title: t("Add"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required(t("Title is required")),
    description: Yup.string().trim(),
    category: Yup.string().trim().required(t("Category is required")),
    total_levels: Yup.number()
      .min(1, t("Levels must be at least 1"))
      .required(t("Total levels is required")),
    difficulty_level: Yup.string().required(t("Difficulty level is required")),
    thumbnail: Yup.mixed()
      .required(t("Thumbnail is required"))
      .test("fileSize", t("image_max_size_error"), (value) => {
        if (!value || typeof value === "string") return true;
        return value.size <= 2 * 1024 * 1024; // 2MB
      }),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      category: "General",
      total_levels: 15,
      difficulty_level: "Easy",
      thumbnail: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("category", values.category);
      formData.append("total_levels", values.total_levels);
      formData.append("difficulty_level", values.difficulty_level);
      formData.append("thumbnail", values.thumbnail);

      gameAdd(formData)
        .then((response) => {
          globalLoader(false);
          setSubmitted(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            resetForm();
            navigate("/admin/game-management/game/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitted(false);
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("Add Game")}</h6>
                <div className="row row-sm">
                  {/* Title */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Title")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                      className="form-control"
                      placeholder={t("Enter game title")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="title" form={formik} />
                    </span>
                  </div>

                  {/* Category */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Category")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="text"
                      name="category"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.category}
                      className="form-control"
                      placeholder={t("Enter category")}
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="category" form={formik} />
                    </span>
                  </div>

                  {/* Total Levels */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Total Levels")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="number"
                      name="total_levels"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.total_levels}
                      className="form-control"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="total_levels" form={formik} />
                    </span>
                  </div>

                  {/* Difficulty Level */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Difficulty Level")}:
                      <span className="requirestar">*</span>
                    </label>
                    <select
                      name="difficulty_level"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.difficulty_level}
                      className="form-control"
                    >
                      <option value="Easy">{t("Easy")}</option>
                      <option value="Medium">{t("Medium")}</option>
                      <option value="Hard">{t("Hard")}</option>
                    </select>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="difficulty_level" form={formik} />
                    </span>
                  </div>

                  {/* Description */}
                  <div className="col-md-12 form-group">
                    <label className="text-left d-flex">
                      {t("Description")}:
                    </label>
                    <textarea
                      name="description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                      className="form-control"
                      rows="4"
                      placeholder={t("Enter description")}
                    ></textarea>
                  </div>

                  {/* Thumbnail */}
                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">
                      {t("Thumbnail")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      name="thumbnail"
                      className="form-control"
                      onChange={(e) =>
                        formik.setFieldValue(
                          "thumbnail",
                          e.target.files[0] || "",
                        )
                      }
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="thumbnail" form={formik} />
                    </span>
                  </div>

                  {/* Buttons */}
                  <div className="col-md-12 mt-3">
                    <button
                      className="btn btn-info mr-2"
                      type="submit"
                      disabled={submitted}
                    >
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default GameAdd;
